import { z } from "zod";
/**
 * Place sustainability schemas — the shared contract for the Phase 3 surface
 * (apps/api/src/routes/places/placeSustainability.ts):
 *   * place_certifications CRUD (GRESB BC1 building certifications)
 *   * place_epc CRUD (energy performance certificates, 1:N)
 * See docs/places/phase-3.md and docs/BACKEND_API.md.
 */
/** EPC scheme codes — a local option list (R5 pattern; no hub concept). Lives
 * here (not placeClassification.ts) because it is server-validated via z.enum. */
export const PLACE_EPC_SCHEME_OPTIONS = [
    "nl",
    "fr_dpe",
    "uk_epc",
    "ie_ber",
    "de_energieausweis",
    "be_epc",
    "generic_eu",
];
export const epcSchemeSchema = z.enum(PLACE_EPC_SCHEME_OPTIONS);
export const certificationTypeSchema = z.enum([
    "design_construction",
    "operational",
]);
/** A DATE-only value (the DB columns are `date`), as YYYY-MM-DD. */
const dateString = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "expected a YYYY-MM-DD date");
const nullableText = (max) => z.string().max(max).nullable().optional();
// ---------------------------------------------------------------------------
// Certifications
// ---------------------------------------------------------------------------
const certificationCore = {
    scheme_other: nullableText(200),
    scheme_version: nullableText(100),
    level: nullableText(200),
    score: z.number().finite().min(0).nullable().optional(),
    floor_area_coverage_pct: z.number().finite().min(0).max(100).nullable().optional(),
    certification_year: z.number().int().min(1900).max(2100).nullable().optional(),
    certifying_body: nullableText(200),
    certificate_number: nullableText(200),
    issue_date: dateString.nullable().optional(),
    expiry_date: dateString.nullable().optional(),
    notes: nullableText(2000),
    document_file_id: z.string().uuid().nullable().optional(),
};
/** expiry_date must not precede issue_date when both are present. */
const certExpiryRefine = (d) => !d.issue_date || !d.expiry_date || d.expiry_date >= d.issue_date;
/** scheme_other belongs only with scheme = 'Other' — and is required there. */
export const createPlaceCertificationSchema = z
    .object({
    scheme: z.string().min(1).max(200),
    certification_type: certificationTypeSchema,
    ...certificationCore,
})
    .refine(certExpiryRefine, {
    message: "expiry_date cannot precede issue_date",
    path: ["expiry_date"],
})
    .refine((d) => d.scheme !== "Other" || (!!d.scheme_other && d.scheme_other.trim().length > 0), { message: "scheme_other is required when scheme is 'Other'", path: ["scheme_other"] })
    .refine((d) => d.scheme === "Other" || d.scheme_other == null, {
    message: "scheme_other is only allowed when scheme is 'Other'",
    path: ["scheme_other"],
});
export const updatePlaceCertificationSchema = z
    .object({
    scheme: z.string().min(1).max(200).optional(),
    certification_type: certificationTypeSchema.optional(),
    ...certificationCore,
})
    .refine((d) => Object.keys(d).length > 0, { message: "empty update" })
    .refine(certExpiryRefine, {
    message: "expiry_date cannot precede issue_date",
    path: ["expiry_date"],
});
// ---------------------------------------------------------------------------
// EPC
// ---------------------------------------------------------------------------
const epcCore = {
    epc_score: z.number().finite().min(0).nullable().optional(),
    epc_expiry_date: dateString.nullable().optional(),
    certificate_reference: nullableText(200),
    notes: nullableText(2000),
    document_file_id: z.string().uuid().nullable().optional(),
};
const epcExpiryRefine = (d) => !d.epc_issue_date || !d.epc_expiry_date || d.epc_expiry_date >= d.epc_issue_date;
export const createPlaceEpcSchema = z
    .object({
    epc_scheme: epcSchemeSchema,
    epc_class: z.string().min(1).max(20),
    epc_issue_date: dateString,
    ...epcCore,
})
    .refine(epcExpiryRefine, {
    message: "epc_expiry_date cannot precede epc_issue_date",
    path: ["epc_expiry_date"],
});
export const updatePlaceEpcSchema = z
    .object({
    epc_scheme: epcSchemeSchema.optional(),
    epc_class: z.string().min(1).max(20).optional(),
    epc_issue_date: dateString.optional(),
    ...epcCore,
})
    .refine((d) => Object.keys(d).length > 0, { message: "empty update" })
    .refine(epcExpiryRefine, {
    message: "epc_expiry_date cannot precede epc_issue_date",
    path: ["epc_expiry_date"],
});
//# sourceMappingURL=placeSustainability.js.map